import React, { useEffect, useMemo, useState } from "react";
import { ChatClient } from "@azure/communication-chat";
import { AzureCommunicationTokenCredential } from "@azure/communication-common";
import {
  Card, CardContent, Typography, Button, Chip, Stack, Divider, Alert, Box,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import PersonIcon from "@mui/icons-material/Person";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import useChatJobOfferPolling from "./offerpollingChats.jsx";
import { acceptIvrJob, removeChatbot } from "../../services/ivrModule/jobServices.js";
import { completeIvrAssignment, closeIvrAssignment } from "../../services/ivrModule/assignmentServices.js";

// IMPORTANT: endpoint URL (no connection string) — normalize trailing slashes
const ACS_ENDPOINT_RAW = "https://ai-sms-mt-dev.unitedstates.communication.azure.com/";
const ACS_ENDPOINT = ACS_ENDPOINT_RAW.replace(/\/+$/, "");

export default function OutboundChatPanel({onCustomerGuid}) {
  // Restore on refresh (from localStorage)
  const [activeThreadId, setActiveThreadId] = useState(
    () => localStorage.getItem("activeThreadId") || null
  );
  const [assignmentId, setAssignmentId] = useState(
    () => localStorage.getItem("assignmentId") || null
  );

  const [chatClient, setChatClient] = useState(null);  // agent client (for leave, etc.)
  const [joining, setJoining] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [error, setError] = useState("");

  // Context
  const workerId = useMemo(() => localStorage.getItem("workerid") || "", []);
  const agentUserId = useMemo(
    () => localStorage.getItem("acsIdentity") || localStorage.getItem("ACSIdentity") || "",
    []
  );
  const agentDisplayName = useMemo(
    () => localStorage.getItem("agentDisplayName") || localStorage.getItem("name") || "Agent",
    []
  );

  // Offer polling re-runs automatically after refresh
  const { jobOffer, setJobOffer, blink } = useChatJobOfferPolling(workerId, 15000);

  // Initialize an agent ChatClient on mount (so after a refresh you can still act on the thread)
  useEffect(() => {
    const token = localStorage.getItem("ACSIdentityToken");
    if (!token) return; // we'll error later only if needed
    try {
      const cred = new AzureCommunicationTokenCredential(token);
      const client = new ChatClient(ACS_ENDPOINT, cred);
      setChatClient(client);
    } catch (e) {
      console.error("Agent ChatClient init failed:", e);
      // do not set global error yet; user can still accept a new offer
    }
  }, []);

  // Persist thread/assignment to localStorage so refresh restores them
  useEffect(() => {
    if (activeThreadId) localStorage.setItem("activeThreadId", activeThreadId);
    else localStorage.removeItem("activeThreadId");
  }, [activeThreadId]);

  useEffect(() => {
    if (assignmentId) localStorage.setItem("assignmentId", assignmentId);
    else localStorage.removeItem("assignmentId");
  }, [assignmentId]);

  // Accept the offer, add agent via service/bot token, remove bot, then switch to agent token for UI
  const acceptAndJoin = async () => {
    if (!jobOffer) return;
    setJoining(true);
    setError("");

    try {
      // 1) Accept the offer on your backend
      const res = await acceptIvrJob({
        workerId,
        offerId: jobOffer.offerId || jobOffer.id,
        channelReference: jobOffer.channelReference, // often the threadId
        customerAcsId: jobOffer.customerAcsId,
        agentAcsId: agentUserId,
      });

      // Unwrap Axios response
      const payload = res?.data ?? {};
      const policy  = payload?.policy ?? {};

      // 2) Resolve threadId
      const threadId =
        jobOffer.channelReference ||
        jobOffer?.labels?.threadId ||
        payload?.threadId ||
        payload?.labels?.threadId;

      if (!threadId) throw new Error("No threadId available on offer/response.");

      // 3) Service/bot token (must already be in the thread)
      const serviceToken =
        jobOffer?.userToken ||
        payload?.userToken ||
        policy?.userToken ||
        null;

      if (!serviceToken) {
        throw new Error("Missing userToken on offer/response for service/bot identity.");
      }

      // 3a) Service adds the agent to the thread
      const serviceCred = new AzureCommunicationTokenCredential(serviceToken);
      const serviceClient = new ChatClient(ACS_ENDPOINT, serviceCred);
      const threadAsService = serviceClient.getChatThreadClient(threadId);

      await threadAsService.addParticipants({
        participants: [{ id: { communicationUserId: agentUserId }, displayName: agentDisplayName }],
      });

      // Remove the visible bot (backend route handles actor permissions & safety)
      try { await removeChatbot({ threadId }); } catch {}

      // Optional system message
      try { await threadAsService.sendMessage({ content: "Connecting you to a live agent…" }); } catch {}

      // 4) Switch to the AGENT identity for ongoing UI
      const agentToken = localStorage.getItem("ACSIdentityToken");
      if (!agentToken) throw new Error("Missing agent ACSIdentityToken in localStorage.");

      const agentCred = new AzureCommunicationTokenCredential(agentToken);
      const agentClient = new ChatClient(ACS_ENDPOINT, agentCred);
      setChatClient(agentClient);

      // Optional: greet as agent
      try {
        await agentClient.getChatThreadClient(threadId).sendMessage({
          content: `Hi, this is ${agentDisplayName}.`,
        });
      } catch {}

      // 5) Update + persist UI state so refresh restores
      setActiveThreadId(threadId);
      setAssignmentId(policy?.assignmentId ?? payload?.assignmentId ?? null);
      setJobOffer(null);
      // localStorage effects above will persist these
    } catch (e) {
      console.error("acceptAndJoin error:", e);
      setError(e?.message || String(e));
    } finally {
      setJoining(false);
    }
  };

  // Leave chat and complete assignment (as AGENT)
  const leaveChat = async () => {
    if (!activeThreadId) return;
    setLeaving(true);
    setError("");

    try {
      // Recreate agent client if missing (e.g., after a refresh and init failed earlier)
      let client = chatClient;
      if (!client) {
        const agentToken = localStorage.getItem("ACSIdentityToken");
        if (!agentToken) throw new Error("Missing agent ACSIdentityToken in localStorage.");
        client = new ChatClient(ACS_ENDPOINT, new AzureCommunicationTokenCredential(agentToken));
        setChatClient(client);
      }

      const threadClient = client.getChatThreadClient(activeThreadId);

      if (agentUserId) {
        await threadClient.removeParticipant({ communicationUserId: agentUserId });
      }

      if (assignmentId) {
        try { await completeIvrAssignment({ assignmentId, workerId, offerId: jobOffer.offerId }); 
              await closeIvrAssignment({ assignmentId, workerId, offerId: jobOffer.offerId });
        } catch (inner) {
          console.warn("completeIvrAssignment failed (non-fatal):", inner);
        }
      }

      // Clear UI and persisted state
      setActiveThreadId(null);
      setAssignmentId(null);
      setJobOffer(null);
    } catch (e) {
      console.error("leaveChat error:", e);
      setError(e?.message || String(e));
    } finally {
      setLeaving(false);
    }
  };

  const OfferCard = () => (
    <Card sx={{ maxWidth: 520, boxShadow: 3, borderLeft: blink ? "6px solid" : "6px solid transparent" }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <MarkEmailUnreadIcon />
          <Typography variant="h6">Incoming Chat Offer</Typography>
          {blink && <Chip label="NEW" size="small" color="primary" />}
        </Stack>
        <Divider sx={{ my: 1 }} />
        <Stack spacing={1}>
          <Row label="Offer Id" value={jobOffer?.offerId || jobOffer?.id} />
          <Row label="Thread Id" value={jobOffer?.channelReference || jobOffer?.labels?.threadId || "(not provided)"} />
          <Row label="Customer ACS Id" value={jobOffer?.customerAcsId || "(unknown)"} />
          {/* Debug helper (do NOT log token value) */}
          <Row label="Has userToken" value={jobOffer?.userToken ? "yes" : "no"} />
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button variant="contained" onClick={acceptAndJoin} disabled={joining} startIcon={<ChatIcon />}>
            {joining ? "Joining…" : "Accept & Join"}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );

  const ActiveChatCard = () => (
    <Card sx={{ maxWidth: 520, boxShadow: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <PersonIcon />
          <Typography variant="h6">Chat</Typography>
        </Stack>
        <Divider sx={{ my: 1 }} />
        <Stack sx={{ width: "100%" }}>
          <Row value={activeThreadId} />
          {/* <Row label="Assignment Id" value={assignmentId ?? "(none)"} />
          <Row label="Agent" value={`${agentDisplayName} (${agentUserId || "?"})`} /> */}
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={leaveChat} disabled={leaving}>
            {leaving ? "Leaving…" : "Leave Chat"}
          </Button>
          <Button
            variant="text"
            onClick={() => {
              // quick reset if the thread ended server-side but UI didn't catch it
              setActiveThreadId(null);
              setAssignmentId(null);
              setJobOffer(null);
            }}
          >
            Reset
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 1, fontFamily: "Inter, system-ui, Arial" }}>
      <Typography  gutterBottom>Chat Panel</Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={2}>
        {!activeThreadId && jobOffer && <OfferCard />}
        {activeThreadId && <ActiveChatCard />}
        {!activeThreadId && !jobOffer && (
          <Card sx={{ maxWidth: 520, boxShadow: 0, mt: 2, border: "1px dashed #ccc" }}>
            <CardContent>
              <Typography variant="body1">Waiting for chat offers…</Typography>
            </CardContent>
          </Card>
        )}
      </Stack>
    </Box>
  );
}

function Row({ label, value }) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      {/* <Typography variant="body2" sx={{ minWidth: 130, color: "text.secondary" }}>{label}</Typography> */}
      <Typography variant="body2" sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace", fontSize: "8px" }}>
        {String(value)}
      </Typography>
    </Stack>
  );
}
